import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import { useFormatCurrency } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import { AccordionItem } from "@krea/customer-features/shared-components/accordion/AccordionItem";
import Button from "@krea/customer-features/shared-components/button";
import { ResultContent } from "@krea/customer-features/shared-components/preliminary-bid-result/ResultContent";
import { Text } from "@krea/customer-features/shared-components/text";
import { useLoanApplicationFormStore } from "@krea/customer-features/store/loan-application-form/hooks";
import {
  googleCalendarUrlSE,
  googleCalendarUrlFI,
} from "@krea/shared/constants/index";
import { CountryCode } from "@krea/shared/types/common";

const CALENDAR_URL_MAP: Record<CountryCode, string> = {
  [CountryCode.SE]: `${googleCalendarUrlSE}##follow`,
  [CountryCode.FI]: `${googleCalendarUrlFI}##follow`,
};

const EligibilityCheckResult = ({
  isOnResultPage = false,
}: {
  isOnResultPage: boolean;
}) => {
  const { t } = useTranslation();
  const [formattedCurrencyValue] = useFormatCurrency({
    currency: settings.currency,
  });

  const { organisationName, eligibilityCheck } = useLoanApplicationFormStore(
    useShallow((state) => ({
      organisationName: state.organisation.name,
      eligibilityCheck: state.eligibilityCheck,
    })),
  );

  const probabilityOptions = {
    low: t("preliminaryBidForm.probabilityOptions.low"),
    medium: t("preliminaryBidForm.probabilityOptions.medium"),
    high: t("preliminaryBidForm.probabilityOptions.high"),
  };

  const estimatedAmount = eligibilityCheck?.estimatedMaxLoanAmount;

  const isEdgeCase = eligibilityCheck?.useEdgeCaseLogic;

  const probabilityKey =
    eligibilityCheck &&
    (eligibilityCheck.probabilityLoanApproved as keyof typeof probabilityOptions);

  const probability = probabilityKey
    ? probabilityOptions[probabilityKey]
    : null;

  const reasonForLowProbabilityTextMap: Record<CountryCode, string> = {
    [CountryCode.SE]: t(
      "preliminaryBidForm.reasonForLowProbabilityDescription2Swedish",
    ),
    [CountryCode.FI]: t(
      "preliminaryBidForm.reasonForLowProbabilityDescription2Finnish",
    ),
  };

  return !isEmpty(eligibilityCheck) ? (
    <>
      {isOnResultPage ? (
        <ResultContent
          headingContent={
            <>
              <Text size="sm" className="tw-font-normal">
                {organisationName}
              </Text>

              <>
                {isEdgeCase ? (
                  <Text size="lg" className="tw-font-bold">
                    {t("eligibilityCheckForm.qualifiedForLoanAndAdvisory")}
                  </Text>
                ) : (
                  <>
                    {(probability === probabilityOptions.medium ||
                      probability === probabilityOptions.low) && (
                      <Text size="lg" className="tw-font-bold">
                        {t("eligibilityCheckForm.likelihoodToReceiveOffers")}
                        {probabilityOptions[probabilityKey!]}
                      </Text>
                    )}
                    {probability === probabilityOptions.high && (
                      <Text size="lg" className="tw-font-bold">
                        {t("eligibilityCheckForm.highLikelihood")}
                      </Text>
                    )}
                  </>
                )}
              </>
            </>
          }
          probability={probability}
          probabilityOptions={probabilityOptions}
          isEdgeCase={isEdgeCase}
        >
          <>
            {isEdgeCase ? (
              <>
                <AccordionItem
                  titleStyling="tw-font-normal tw-text-base"
                  className="tw-mb-4"
                  title={t("eligibilityCheckForm.whatIsAdvisory")}
                >
                  <Text size="sm">
                    {t("eligibilityCheckForm.advisoryDescription1")}
                    <br />
                    <br />
                    {t("eligibilityCheckForm.advisoryDescription2")}
                    <br />
                    <br />
                    {t("eligibilityCheckForm.advisoryDescription3")}
                  </Text>
                </AccordionItem>

                <div className="tw-mb-5 tw-flex tw-items-center tw-justify-between tw-rounded tw-border tw-border-solid tw-px-5 tw-py-4">
                  <Text size="md" className="tw-font-normal">
                    {t("eligibilityCheckForm.wantToKnowMore")}
                  </Text>

                  <a
                    href={CALENDAR_URL_MAP[settings.countryCode]}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="text" size="sm" className="tw-font-bold">
                      {t("preliminaryBidForm.bookAMeeting")}
                    </Button>
                  </a>
                </div>
              </>
            ) : (
              <>
                {probability === probabilityOptions.high && (
                  <div className="tw-pb-4">
                    <div className="tw-mb-5 tw-rounded tw-border tw-border-solid tw-px-5 tw-py-4">
                      <Text size="md" className="tw-font-normal">
                        {t("eligibilityCheckForm.approximateCreditLimit")}
                        {formattedCurrencyValue(estimatedAmount!)}
                      </Text>
                    </div>

                    <AccordionItem
                      titleStyling="tw-font-normal tw-text-md"
                      title={t("eligibilityCheckForm.seeHowWeCalculated")}
                    >
                      <Text size="sm">
                        {t("eligibilityCheckForm.howWeCalculatedDescription1")}{" "}
                        <br />
                        <br />
                        {t("eligibilityCheckForm.howWeCalculatedDescription2")}
                      </Text>
                    </AccordionItem>
                  </div>
                )}
                {probability === probabilityOptions.medium && (
                  <div className="tw-pb-4">
                    <div className="tw-mb-5 tw-rounded tw-border tw-border-solid tw-px-5 tw-py-4">
                      <Text size="md" className="tw-font-normal">
                        {t("eligibilityCheckForm.approximateCreditLimit")}
                        {formattedCurrencyValue(estimatedAmount!)}
                      </Text>
                    </div>

                    <AccordionItem
                      titleStyling="tw-font-normal tw-text-md"
                      title={t("eligibilityCheckForm.seeHowWeCalculated")}
                    >
                      <Text size="sm">
                        {t("eligibilityCheckForm.howWeCalculatedDescription1")}{" "}
                        <br />
                        <br />
                        {t("eligibilityCheckForm.howWeCalculatedDescription2")}
                      </Text>
                    </AccordionItem>
                  </div>
                )}
                {probability === probabilityOptions.low && (
                  <div>
                    <AccordionItem
                      titleStyling="tw-font-normal tw-text-md"
                      className="tw-mb-4"
                      title={t("preliminaryBidForm.why")}
                    >
                      <Text size="sm">
                        {t(
                          "preliminaryBidForm.reasonForLowProbabilityDescription1",
                        )}
                        <br /> <br />
                        <ul>
                          <li>
                            {
                              reasonForLowProbabilityTextMap[
                                settings.countryCode
                              ]
                            }
                          </li>
                          <li>
                            {t(
                              "preliminaryBidForm.reasonForLowProbabilityDescription3",
                            )}
                          </li>
                          <li>
                            {t(
                              "preliminaryBidForm.reasonForLowProbabilityDescription4",
                            )}
                          </li>
                        </ul>
                        {t(
                          "preliminaryBidForm.reasonForLowProbabilityDescription5",
                        )}
                        <br /> <br />
                        {t(
                          "preliminaryBidForm.reasonForLowProbabilityDescription6",
                        )}
                      </Text>
                    </AccordionItem>
                  </div>
                )}
              </>
            )}
          </>
        </ResultContent>
      ) : null}
    </>
  ) : null;
};

export default EligibilityCheckResult;
